import React, { useCallback, useEffect, useMemo, useState, useId } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Cell,
  LabelList,
  Text,
  AreaChart,
  Area,
} from 'recharts';
import { useDuckDB } from '../context/LazyDuckDBContext';
import { useKpiMetadata } from '../services/useKpiMetadata';

interface CompareSchoolsFullWidthProps {
  kommunId: string;
  kpiId: string;
  skolaId: string | null;
  selectedYear: number;
  compareScope?: 'municipality' | 'national';
  onSelectEntity?: (id: string) => void;
}

type RankingRow = {
  ou_id: string;
  ou_name: string;
  value: number;
  rank_value?: number | null;
  comparison_average?: number | null;
  category?: string;
  municipality_id?: string | null;
  municipality_name?: string | null;
};

type ChartItem = {
  id: string;
  name: string;
  value: number;
  highlighted: boolean;
  rank: number | null;
  delta: number | null;
  raw: RankingRow;
};

type DensityPoint = {
  value: number;
  density: number;
};

const computeDomain = (values: number[], paddingFraction = 0.07): [number, number] => {
  if (!values.length) {
    return [0, 1];
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const padding = Math.max(span * paddingFraction, 1);
  const paddedMin = min - padding;
  const paddedMax = max + padding;
  const roundedMin = Math.floor(paddedMin / 5) * 5;
  const roundedMax = Math.ceil(paddedMax / 5) * 5;
  return [roundedMin, roundedMax];
};

const truncateLabel = (value: string, maxChars = 26): string => {
  if (value.length <= maxChars) {
    return value;
  }
  return `${value.slice(0, Math.max(0, maxChars - 1))}…`;
};

const buildDensity = (values: number[], binCount = 24): DensityPoint[] => {
  if (!values.length) {
    return [];
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const bins = Math.max(6, binCount);
  const step = span / bins;
  if (!Number.isFinite(step) || step === 0) {
    return [];
  }

  const counts = Array.from({ length: bins }, (_, index) => ({
    value: min + index * step + step / 2,
    density: 0,
  }));

  values.forEach((rawValue) => {
    const relative = rawValue - min;
    const idx = Math.min(bins - 1, Math.max(0, Math.floor(relative / step)));
    counts[idx].density += 1;
  });

  const maxCount = Math.max(...counts.map((c) => c.density));
  return counts.map((entry) => ({
    value: entry.value,
    density: maxCount > 0 ? entry.density / maxCount : 0,
  }));
};

const CompareSchoolsFullWidthHORIZONTAL: React.FC<CompareSchoolsFullWidthProps> = ({
  kommunId,
  kpiId,
  skolaId,
  selectedYear,
  compareScope = 'municipality',
  onSelectEntity,
}) => {
  const { query } = useDuckDB();
  const { getKpiInfo } = useKpiMetadata();
  const [rows, setRows] = useState<RankingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntityHasData, setSelectedEntityHasData] = useState(true);
  const [comparisonAverage, setComparisonAverage] = useState<number | null>(null);
  const [densityData, setDensityData] = useState<DensityPoint[]>([]);
  const [densityDomain, setDensityDomain] = useState<[number, number] | null>(null);
  const [selectedDensityValue, setSelectedDensityValue] = useState<number | null>(null);
  const isNational = compareScope === 'national';

  const currentKpi = useMemo(() => getKpiInfo(kpiId), [getKpiInfo, kpiId]);
  const unitLabel = currentKpi?.helperText?.trim() ?? '';
  const rawGradientId = useId();
  const gradientId = useMemo(() => rawGradientId.replace(/:/g, ''), [rawGradientId]);

  const handleSelect = useCallback(
    (id: string) => {
      if (onSelectEntity) {
        onSelectEntity(id);
      }
    },
    [onSelectEntity]
  );

  const fetchRankingData = useCallback(async () => {
    if (!kpiId) {
      return;
    }

    if (!isNational && !kommunId) {
      setRows([]);
      setLoading(false);
      setSelectedEntityHasData(false);
      setComparisonAverage(null);
      setDensityData([]);
      setDensityDomain(null);
      setSelectedDensityValue(null);
      return;
    }

    setLoading(true);
    setRows([]);
    setComparisonAverage(null);
    setSelectedEntityHasData(true);
    setDensityData([]);
    setDensityDomain(null);
    setSelectedDensityValue(null);

    try {
      const cteMunicipalityFilter = !isNational && kommunId ? `AND municipality_id = '${kommunId}'` : '';
      const joinMunicipalityFilter = !isNational && kommunId ? `AND v.municipality_id = '${kommunId}'` : '';

      const sql = `
        WITH latest AS (
          SELECT MAX(year) AS y
          FROM gold_kpi_trend
          WHERE entity_type = 'org_unit'
            AND kpi_id = '${kpiId}'
            AND gender = 'T'
            ${cteMunicipalityFilter}
            AND year <= ${selectedYear}
            AND value IS NOT NULL
        ),
        municipalities AS (
          SELECT DISTINCT municipality_id, municipality_name
          FROM gold_kpi_muni_year
        )
        SELECT
          v.entity_id AS ou_id,
          v.entity_name AS ou_name,
          v.municipality_id AS municipality_id,
          m.municipality_name AS municipality_name,
          v.value AS value,
          DENSE_RANK() OVER (ORDER BY v.value DESC) AS rank_value,
          AVG(v.value) OVER () AS comparison_average
        FROM latest l
        JOIN gold_kpi_trend v
          ON v.entity_type = 'org_unit'
         AND v.kpi_id = '${kpiId}'
         AND v.gender = 'T'
         AND v.year = l.y
         ${joinMunicipalityFilter}
        LEFT JOIN municipalities m
          ON v.municipality_id = m.municipality_id
        WHERE v.value IS NOT NULL
        ORDER BY v.value DESC;
      `;

      const data = await query(sql);
      const normalized = (data as any[])
        .map((row) => ({
          ou_id: String(row.ou_id),
          ou_name: String(row.ou_name),
          municipality_id: row.municipality_id ? String(row.municipality_id) : null,
          municipality_name: row.municipality_name ? String(row.municipality_name) : null,
          value: Number(row.value),
          rank_value: row.rank_value != null ? Number(row.rank_value) : null,
          comparison_average:
            row.comparison_average != null ? Number(row.comparison_average) : null,
        }))
        .filter((row) => Number.isFinite(row.value));

      if (!isNational) {
        const hasSelected = skolaId ? normalized.some((row) => row.ou_id === skolaId) : true;
        setSelectedEntityHasData(hasSelected);

        const sortedDesc = [...normalized].sort((a, b) => b.value - a.value);
        const sortedAsc = [...normalized].sort((a, b) => a.value - b.value);

        const top = sortedDesc.slice(0, 3);
        const bottom = sortedAsc.slice(0, 3);
        const subsetMap = new Map<string, RankingRow>();

        top.forEach((row) => subsetMap.set(row.ou_id, { ...row, category: 'top' }));
        bottom.forEach((row) => subsetMap.set(row.ou_id, { ...row, category: 'bottom' }));

        let selectedValue: number | null = null;
        if (skolaId) {
          const selectedRow = normalized.find((row) => row.ou_id === skolaId);
          if (selectedRow) {
            subsetMap.set(selectedRow.ou_id, { ...selectedRow, category: 'selected' });
            selectedValue = selectedRow.value;
          }
        }

        const subset = Array.from(subsetMap.values()).sort((a, b) => b.value - a.value);
        setRows(subset);
        setComparisonAverage(
          normalized.length ? normalized[0].comparison_average ?? null : null
        );

        const allValues = normalized.map((row) => row.value);
        if (allValues.length) {
          setDensityData(buildDensity(allValues));
          setDensityDomain([Math.min(...allValues), Math.max(...allValues)]);
          setSelectedDensityValue(selectedValue);
        }
      } else {
        const sortedDesc = [...normalized].sort((a, b) => b.value - a.value);
        const sortedAsc = [...normalized].sort((a, b) => a.value - b.value);

        const top = sortedDesc.slice(0, 3);
        const bottom = sortedAsc.slice(0, 3);
        const subsetMap = new Map<string, RankingRow>();

        top.forEach((row) => subsetMap.set(row.ou_id, { ...row, category: 'top' }));
        bottom.forEach((row) => subsetMap.set(row.ou_id, { ...row, category: 'bottom' }));

        let selectedValue: number | null = null;
        if (skolaId) {
          const selectedRow = normalized.find((row) => row.ou_id === skolaId);
          if (selectedRow) {
            subsetMap.set(selectedRow.ou_id, { ...selectedRow, category: 'selected' });
            selectedValue = selectedRow.value;
          }
        }

        const subset = Array.from(subsetMap.values()).sort((a, b) => b.value - a.value);
        const hasSelected = skolaId ? normalized.some((row) => row.ou_id === skolaId) : true;
        setSelectedEntityHasData(hasSelected);
        setRows(subset);
        setComparisonAverage(sortedDesc.length ? sortedDesc[0].comparison_average ?? null : null);

        const allValues = normalized.map((row) => row.value);
        if (allValues.length) {
          setDensityData(buildDensity(allValues));
          setDensityDomain([Math.min(...allValues), Math.max(...allValues)]);
          setSelectedDensityValue(selectedValue);
        } else {
          setDensityData([]);
          setDensityDomain(null);
          setSelectedDensityValue(null);
        }
      }
    } catch (error) {
      console.error('Error fetching ranking data:', error);
      setRows([]);
      setComparisonAverage(null);
      setSelectedEntityHasData(false);
      setDensityData([]);
      setDensityDomain(null);
      setSelectedDensityValue(null);
    } finally {
      setLoading(false);
    }
  }, [isNational, query, kpiId, kommunId, selectedYear, skolaId]);

  useEffect(() => {
    void fetchRankingData();
  }, [fetchRankingData]);

  const selectedEntityId = skolaId ?? null;

  const chartItems: ChartItem[] = useMemo(
    () =>
      rows.map((row) => {
        const value = row.value;
        const highlighted = selectedEntityId ? row.ou_id === selectedEntityId : false;
        const delta = comparisonAverage != null ? value - comparisonAverage : null;
        const displayName = row.municipality_name
          ? `${row.ou_name} (${row.municipality_name})`
          : row.ou_name;
        return {
          id: row.ou_id,
          name: displayName,
          value,
          highlighted,
          rank: row.rank_value ?? null,
          delta,
          raw: row,
        };
      }),
    [rows, selectedEntityId, comparisonAverage]
  );

  const valueDomain = useMemo(
    () => computeDomain(chartItems.map((item) => item.value)),
    [chartItems]
  );

  const useHorizontalBars = !isNational || chartItems.length <= 30;

  const chartHeight = useMemo(() => {
    if (useHorizontalBars) {
      // For horizontal bars, calculate height based on number of items
      // Each bar needs ~40px height + padding
      const baseHeight = 80; // Top/bottom padding
      const barHeight = 40; // Height per bar
      const calculatedHeight = baseHeight + (chartItems.length * barHeight);
      return Math.max(280, Math.min(calculatedHeight, 600)); // Min 280px, max 600px
    }
    // For vertical bars, use a fixed height that fills the container better
    return 400;
  }, [chartItems.length, useHorizontalBars]);

  const barSize = useMemo(() => {
    if (useHorizontalBars) {
      // For horizontal bars, use a consistent size that looks good
      return 28;
    }
    // For vertical bars, calculate based on available width
    return Math.max(12, Math.min(50, 1000 / Math.max(chartItems.length, 1)));
  }, [useHorizontalBars, chartItems.length]);

  const rankLookup = useMemo(() => {
    const map = new Map<string, number | null>();
    rows.forEach((row) => {
      map.set(row.ou_id, row.rank_value ?? null);
    });
    return map;
  }, [rows]);

  const axisTickFormatter = useCallback((value: string) => truncateLabel(value, 28), []);

  const renderTooltip = useCallback(
    ({ active, payload }: any) => {
      if (!active || !payload || !payload.length) {
        return null;
      }
      const item = payload[0].payload as ChartItem;
      const rank = rankLookup.get(item.id) ?? null;
      const delta = item.delta != null ? item.delta : null;
      return (
        <div className="rounded-lg bg-white dark:bg-slate-800 shadow px-3 py-2 text-[13px] text-ink1">
          <div className="font-medium text-ink1">{item.name}</div>
          <div className="text-ink2">
            {item.value.toFixed(1)}
            {unitLabel ? ` ${unitLabel}` : ''}
            {comparisonAverage != null && delta != null && Number.isFinite(delta) && (
              <>
                {' '}
                ({delta >= 0 ? '+' : ''}
                {delta.toFixed(1)} {isNational ? 'vs riksnitt' : 'vs kommunens snitt'}
                {rank ? `, plats ${rank}` : ''})
              </>
            )}
          </div>
        </div>
      );
    },
    [comparisonAverage, isNational, rankLookup, unitLabel]
  );

  const renderHighlightedLabel = useCallback(
    (props: any) => {
      const { x, y, width, height, value, payload } = props;
      if (!payload?.highlighted || typeof value !== 'number') {
        return null;
      }

      if (useHorizontalBars) {
        const labelX = x + width + 8;
        const labelY = y + height / 2 + 4;
        return (
          <Text
            x={labelX}
            y={labelY}
            textAnchor="start"
            dominantBaseline="middle"
            fontSize={12}
            fontWeight={600}
            fill="var(--ink-1)"
          >
            {value.toFixed(1)}
          </Text>
        );
      }

      const labelX = x + width / 2;
      const labelY = y - 8;
      return (
        <Text
          x={labelX}
          y={labelY}
          textAnchor="middle"
          dominantBaseline="auto"
          fontSize={12}
          fontWeight={600}
          fill="var(--bar-hi-ink)"
        >
          {value.toFixed(1)}
        </Text>
      );
    },
    [useHorizontalBars]
  );

  const selectedEntityMissing = !selectedEntityHasData && Boolean(skolaId);

  if (loading) {
    return (
      <div className="h-[400px] flex items-center justify-center">
        <div className="animate-pulse text-ink2">Laddar jämförelse...</div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="h-[400px] flex items-center justify-center text-ink2">
        <p>Ingen data tillgänglig</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {selectedEntityMissing && (
        <div className="mb-2 flex items-center text-red-600">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium">
            Den valda skolan saknar data för denna indikator
          </span>
        </div>
      )}
      <div className="rounded-xl bg-bgsoft p-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            role="img"
            aria-label={`Stapeldiagram för skoljämförelse ${isNational ? 'mot riket' : 'inom kommunen'}`}
            data={chartItems}
            layout={useHorizontalBars ? 'vertical' : 'horizontal'}
            margin={useHorizontalBars ? { top: 20, right: 40, bottom: 20, left: 160 } : { top: 30, right: 20, bottom: 60, left: 30 }}
            barCategoryGap={useHorizontalBars ? 12 : 4}
          >
            <CartesianGrid horizontal vertical={false} stroke="var(--grid)" />

            {useHorizontalBars ? (
              <XAxis
                type="number"
                domain={valueDomain}
                tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
            ) : (
              <XAxis
                type="category"
                dataKey="name"
                tickFormatter={axisTickFormatter}
                tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
                height={48}
                interval={0}
                angle={-45}
                textAnchor="end"
                axisLine={false}
                tickLine={false}
              />
            )}

            {useHorizontalBars ? (
              <YAxis
                type="category"
                dataKey="name"
                tickFormatter={axisTickFormatter}
                width={132}
                interval={0}
                tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
            ) : (
              <YAxis
                type="number"
                domain={valueDomain}
                tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
                width={48}
                axisLine={false}
                tickLine={false}
              />
            )}

            {comparisonAverage != null && Number.isFinite(comparisonAverage) && (
              useHorizontalBars ? (
                <ReferenceLine
                  x={comparisonAverage}
                  stroke="var(--avg-line)"
                  strokeDasharray="4 4"
                  label={{
                    value: `${isNational ? 'Riks' : 'Kommun'}snitt ${comparisonAverage.toFixed(1)}`,
                    position: 'top',
                    fill: 'var(--ink-2)',
                    fontSize: 11,
                  }}
                />
              ) : (
                <ReferenceLine
                  y={comparisonAverage}
                  stroke="var(--avg-line)"
                  strokeDasharray="4 4"
                  label={{
                    value: `${isNational ? 'Riks' : 'Kommun'}snitt ${comparisonAverage.toFixed(1)}`,
                    position: 'right',
                    fill: 'var(--ink-2)',
                    fontSize: 11,
                  }}
                />
              )
            )}

            <Tooltip cursor={{ fill: 'transparent' }} content={renderTooltip} />

            <Bar
              dataKey="value"
              radius={useHorizontalBars ? [6, 6, 6, 6] : [6, 6, 0, 0]}
              barSize={barSize}
              isAnimationActive={false}
            >
              {chartItems.map((item) => (
                <Cell
                  key={item.id}
                  fill={item.highlighted ? 'var(--bar-hi)' : 'var(--bar-muted)'}
                  fillOpacity={item.highlighted ? 1 : 0.8}
                  stroke={item.highlighted ? 'var(--focus-ring)' : 'transparent'}
                  strokeWidth={item.highlighted ? 1.5 : 0}
                  style={{ cursor: onSelectEntity ? 'pointer' : 'default' }}
                  tabIndex={0}
                  role="presentation"
                  aria-label={`${item.name} ${item.value.toFixed(1)}${unitLabel ? ` ${unitLabel}` : ''}${item.highlighted ? ' (vald)' : ''}`}
                  onClick={() => handleSelect(item.id)}
                  onKeyDown={(event) => {
                    if (!onSelectEntity) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleSelect(item.id);
                    }
                  }}
                />
              ))}
              <LabelList dataKey="value" content={renderHighlightedLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {densityData.length > 0 && densityDomain && (
        <div className="rounded-xl bg-white/60 p-4 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900/40 dark:ring-slate-700">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink2">
              Täthetskurva {isNational ? '(Riket)' : '(Kommun)'}
            </h3>
            {selectedDensityValue != null && (
              <span className="text-xs text-ink2">
                Vald skola: {selectedDensityValue.toFixed(1)}
                {unitLabel ? ` ${unitLabel}` : ''}
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={densityData} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <defs>
                <linearGradient id={`density-${gradientId}`} x1="0" x2="0" y1="0" y2="1">
                  <stop offset="5%" stopColor="var(--bar-muted-60)" stopOpacity={0.85} />
                  <stop offset="95%" stopColor="var(--bar-muted-60)" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <XAxis
                type="number"
                dataKey="value"
                domain={densityDomain}
                tick={{ fill: 'var(--ink-2)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide domain={[0, 1]} />
              <Area
                type="monotone"
                dataKey="density"
                stroke="var(--bar-muted-60)"
                fill={`url(#density-${gradientId})`}
                strokeWidth={1.5}
              />
              {selectedDensityValue != null && (
                <ReferenceLine
                  x={selectedDensityValue}
                  stroke="var(--bar-hi)"
                  strokeWidth={3}
                  label={{
                    value: 'Din skola',
                    fill: 'var(--bar-hi)',
                    fontSize: 11,
                    position: 'insideTopRight',
                    dx: 2,
                    dy: 12,
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-[12px] text-ink2">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm" style={{ background: 'var(--bar-hi)' }} />
          Markerad enhet
        </span>
        {comparisonAverage != null && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-[2px] w-5" style={{ background: 'var(--avg-line)' }} />
            {isNational ? 'Rikssnitt' : 'Kommunens snitt'}
          </span>
        )}
      </div>
    </div>
  );
};

export default CompareSchoolsFullWidthHORIZONTAL;
